import type { MessageListItemDto, EmailClassification } from '@cc/types';

/**
 * Visual + behavioural metadata for each classification. Drives chip variants,
 * section assignment, and sort weights on the inbox.
 */
export const CLASSIFICATION_META: Record<
  EmailClassification,
  {
    label: string;
    variant: 'success' | 'warning' | 'muted' | 'danger' | 'default';
    section: 'reply' | 'activity' | 'cleanup' | 'pending';
  }
> = {
  IMPORTANT_REPLY: { label: 'Reply', variant: 'success', section: 'reply' },
  IMPORTANT_FYI: { label: 'FYI', variant: 'default', section: 'activity' },
  UNCLASSIFIED: { label: 'Pending', variant: 'warning', section: 'pending' },
  ROUTINE: { label: 'Routine', variant: 'muted', section: 'cleanup' },
  NEWSLETTER: { label: 'Newsletter', variant: 'muted', section: 'cleanup' },
  SPAM: { label: 'Spam', variant: 'danger', section: 'cleanup' },
};

/**
 * Tag chip styling. All gray-by-default to avoid color overload; the colour
 * accents are reserved for the classification chip + priority stripe.
 */
export const TAG_LABELS: Record<string, string> = {
  invoice: 'invoice',
  receipt: 'receipt',
  'payment-confirmation': 'payment',
  'meeting-invite': 'meeting',
  'meeting-reschedule': 'meeting change',
  'signature-required': 'sign',
  'decision-needed': 'decision',
  'follow-up-due': 'follow-up',
  promotion: 'promo',
  notification: 'notification',
  'newsletter-content': 'newsletter',
  family: 'family',
  'customer-issue': 'customer',
};

/**
 * Priority-stripe color. P5 red → P1 grey. Maps onto Tailwind border-l-*
 * classes used by the row component.
 */
export function priorityStripeClass(priority: number | null | undefined): string {
  if (!priority) return 'border-l-transparent';
  if (priority >= 5) return 'border-l-red-500';
  if (priority >= 4) return 'border-l-orange-500';
  if (priority >= 3) return 'border-l-amber-500';
  if (priority >= 2) return 'border-l-zinc-500';
  return 'border-l-zinc-700';
}

/**
 * Deterministic avatar background colour from email address. Hash → HSL.
 */
export function avatarColor(email: string): string {
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    hash = (hash * 31 + email.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 35%, 28%)`;
}

export function avatarInitial(message: MessageListItemDto): string {
  const name = message.fromName || message.fromEmail;
  const first = name.trim().charAt(0).toUpperCase();
  return first || '?';
}

/**
 * Bundle key for grouping similar routine/newsletter messages. Returns null
 * for messages that should never bundle (anything actionable).
 *
 * Normalises subject by stripping Re:/Fwd: prefixes, trailing IDs/dates,
 * truncating to first 50 chars. Bundles are formed when 3+ messages share
 * the same key within the visible window.
 */
export function bundleKey(message: MessageListItemDto): string | null {
  if (
    message.classification !== 'ROUTINE' &&
    message.classification !== 'NEWSLETTER'
  ) {
    return null;
  }
  // Never bundle actionable tags even within routine
  if (
    message.tags.includes('signature-required') ||
    message.tags.includes('decision-needed') ||
    message.tags.includes('follow-up-due')
  ) {
    return null;
  }
  const normalized = message.subject
    .toLowerCase()
    .replace(/^\s*(re|fwd?):\s*/i, '')
    .replace(/[#-]?\s*\d{3,}\s*$/, '') // trailing order/invoice numbers
    .replace(/\s+\d{4}-\d{2}-\d{2}\s*$/, '') // trailing dates
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 50);
  return `${message.fromEmail}::${normalized}`;
}

export interface Bundle {
  kind: 'bundle';
  key: string;
  messages: MessageListItemDto[];
  // Representative metadata for the collapsed row
  fromEmail: string;
  fromName: string | null;
  classification: EmailClassification;
  tags: string[];
  mostRecentAt: string;
}

export type InboxRow =
  | {
      kind: 'message';
      message: MessageListItemDto;
      threadCount?: number;
      threadMessageIds?: string[];
    }
  | Bundle;

/**
 * Group messages into bundles or singletons. Runs in two passes:
 *   1. Thread dedup: collapse any (accountId, gmailThreadId) with 2+ messages
 *      down to the latest one, attaching threadCount so the row can show
 *      "+N in thread". This is the primary inbox-readability fix.
 *   2. Subject bundle: among what remains, group ROUTINE/NEWSLETTER messages
 *      from the same sender + normalised subject into a collapsing bundle
 *      when 3+ share it.
 *
 * Order is preserved (so urgency / recent sort still apply); the thread
 * representative inherits the position of its latest message.
 */
export function bundleMessages(messages: MessageListItemDto[]): InboxRow[] {
  // Pass 1: thread dedup. Group all messages by (accountId, gmailThreadId).
  const threadGroups = new Map<string, MessageListItemDto[]>();
  for (const m of messages) {
    const k = threadKey(m);
    const list = threadGroups.get(k) ?? [];
    list.push(m);
    threadGroups.set(k, list);
  }
  const latestPerThread = new Map<string, MessageListItemDto>();
  for (const [k, list] of threadGroups) {
    let latest = list[0]!;
    for (const m of list) {
      if (m.receivedAt > latest.receivedAt) latest = m;
    }
    latestPerThread.set(k, latest);
  }
  const dedupedSeen = new Set<string>();
  const deduped: Array<{
    message: MessageListItemDto;
    threadCount: number;
    ids: string[];
  }> = [];
  for (const m of messages) {
    const k = threadKey(m);
    if (dedupedSeen.has(k)) continue;
    const latest = latestPerThread.get(k)!;
    if (m.id !== latest.id) continue; // emit at the iteration position of the latest
    dedupedSeen.add(k);
    const list = threadGroups.get(k)!;
    deduped.push({
      message: latest,
      threadCount: list.length,
      ids: list.map((x) => x.id),
    });
  }

  // Pass 2: subject-bundle the thread representatives.
  const byBundleKey = new Map<string, MessageListItemDto[]>();
  for (const d of deduped) {
    const k = bundleKey(d.message);
    if (!k) continue;
    const list = byBundleKey.get(k) ?? [];
    list.push(d.message);
    byBundleKey.set(k, list);
  }

  const result: InboxRow[] = [];
  const seenBundleKey = new Set<string>();
  for (const d of deduped) {
    const bk = bundleKey(d.message);
    if (bk && (byBundleKey.get(bk)?.length ?? 0) >= 3) {
      if (seenBundleKey.has(bk)) continue;
      seenBundleKey.add(bk);
      const list = byBundleKey.get(bk)!;
      result.push({
        kind: 'bundle',
        key: bk,
        messages: list,
        fromEmail: list[0]!.fromEmail,
        fromName: list[0]!.fromName,
        classification: list[0]!.classification,
        tags: list[0]!.tags,
        mostRecentAt: list.reduce(
          (acc, x) => (x.receivedAt > acc ? x.receivedAt : acc),
          list[0]!.receivedAt,
        ),
      });
    } else {
      result.push({
        kind: 'message',
        message: d.message,
        threadCount: d.threadCount > 1 ? d.threadCount : undefined,
        threadMessageIds: d.threadCount > 1 ? d.ids : undefined,
      });
    }
  }
  return result;
}

function threadKey(message: MessageListItemDto): string {
  return `${message.accountId}::${message.gmailThreadId}`;
}

/**
 * Partition rows into sections based on the highest-classification message
 * in each row.
 */
export interface SectionedInbox {
  reply: InboxRow[];
  activity: InboxRow[];
  pending: InboxRow[];
  cleanup: InboxRow[];
}

export function sectionRows(rows: InboxRow[]): SectionedInbox {
  const out: SectionedInbox = { reply: [], activity: [], pending: [], cleanup: [] };
  for (const row of rows) {
    const classification =
      row.kind === 'message' ? row.message.classification : row.classification;
    const sec = CLASSIFICATION_META[classification]?.section ?? 'cleanup';
    out[sec].push(row);
  }
  return out;
}
